import { inject, Injectable } from '@angular/core';
import { getMessaging, getToken, onMessage, Messaging, isSupported } from '@angular/fire/messaging';
import { Observable, from, of, EMPTY, defer } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { FirebaseApp } from '@angular/fire/app';
import { AuthService } from './auth.service';
import { ApiNotificationsService } from './api-notifications.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

/** Options for FCM token fetch / user-init flows. */
export interface FcmTokenOptions {
  /**
   * When true (default), POST `notifications/register-token` after obtaining the token.
   * Vendor app sets false and registers via `FcmBackendRegistrationService` only.
   */
  registerWithBackend?: boolean;
}

@Injectable({ providedIn: 'root' })
export class FCMService {
  private authService = inject(AuthService);
  private apiNotifications = inject(ApiNotificationsService);
  /** Injected so the default Firebase app is created before we use it (avoids app/no-app). */
  private firebaseApp = inject(FirebaseApp, { optional: true });
  private messaging: Messaging | null = null;
  // VAPID key - Get this from Firebase Console > Project Settings > Cloud Messaging > Web Push certificates
  // Set this via setVapidKey() method from environment configuration
  private vapidKey: string | null = null;
  
  // Create observable in constructor (injection context) to avoid NG0203 error
  private readonly profile$ = toObservable(this.authService.profile);

  /**
   * Set the VAPID key for FCM token generation
   * Call this from app initialization with the key from environment configuration
   */
  setVapidKey(key: string | null): void {
    this.vapidKey = key;
  }

  async initialize(): Promise<void> {
    try {
      if (!this.firebaseApp) {
        console.warn('[FCM] Firebase app not provided (e.g. no provideFirebaseApp). Skipping FCM.');
        return;
      }
      const isSupportedResult = await isSupported();
      if (!isSupportedResult) {
        console.warn('[FCM] Messaging not supported in this environment');
        return;
      }

      // Register service worker for web push notifications
      if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
          console.log('[FCM] Service worker registered:', registration.scope);
        } catch (error) {
          console.warn('[FCM] Service worker registration failed:', error);
          // Continue anyway - Firebase might handle it
        }
      }

      this.messaging = getMessaging(this.firebaseApp);
      console.log('[FCM] Initialized successfully');
    } catch (error) {
      console.error('[FCM] Initialization failed:', error);
    }
  }

  /** Ensure Messaging exists (child routes can subscribe before root async init finishes). */
  private async ensureMessaging(): Promise<Messaging | null> {
    if (this.messaging) {
      return this.messaging;
    }
    await this.initialize();
    return this.messaging;
  }

  /**
   * Request notification permission (call this early, before login)
   * This only requests permission, doesn't get token yet
   */
  async requestNotificationPermission(): Promise<NotificationPermission> {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      console.warn('[FCM] Notification API not available in this environment');
      return 'denied';
    }

    const currentPermission = Notification.permission;
    console.log('[FCM] Current notification permission:', currentPermission);

    // If already granted or denied, return current status
    if (currentPermission === 'granted' || currentPermission === 'denied') {
      console.log('[FCM] Permission already', currentPermission, '- not requesting again');
      return currentPermission;
    }

    // Request permission (only if in 'default' state)
    console.log('[FCM] Requesting notification permission...');
    try {
      const permission = await Notification.requestPermission();
      console.log('[FCM] Notification permission result:', permission);
      return permission;
    } catch (error) {
      console.error('[FCM] Error requesting notification permission:', error);
      return 'denied';
    }
  }

  /**
   * Get FCM token (call this after permission is granted and user is logged in)
   * This assumes permission is already granted
   */
  async getToken(options: FcmTokenOptions = {}): Promise<string | null> {
    const registerWithBackend = options.registerWithBackend !== false;
    if (!this.messaging) {
      await this.initialize();
      if (!this.messaging) {
        console.warn('[FCM] Messaging not available');
        return null;
      }
    }

    // Check permission first
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission !== 'granted') {
        console.warn('[FCM] Notification permission not granted. Call requestNotificationPermission() first.');
        return null;
      }
    }

    // Skip if VAPID key is not configured
    if (!this.vapidKey) {
      console.warn('[FCM] VAPID key not configured. Skipping token generation.');
      console.warn('[FCM] To enable FCM, add your VAPID key from Firebase Console > Project Settings > Cloud Messaging > Web Push certificates');
      return null;
    }

    try {
      const token = await getToken(this.messaging, { 
        vapidKey: this.vapidKey,
        serviceWorkerRegistration: typeof navigator !== 'undefined' && 'serviceWorker' in navigator
          ? await navigator.serviceWorker.ready
          : undefined
      });
      if (token) {
        console.log('[FCM] Token obtained:', token.substring(0, 20) + '...');
        if (registerWithBackend) {
          await this.saveTokenToBackend(token);
        }
        return token;
      } else {
        console.warn('[FCM] No registration token available');
        return null;
      }
    } catch (error: any) {
      // Handle specific authentication errors gracefully
      if (error?.code === 'messaging/token-subscribe-failed' || 
          error?.message?.includes('authentication credential')) {
        console.warn('[FCM] Token subscription failed - VAPID key may be invalid or Cloud Messaging not configured');
        console.warn('[FCM] Please check your Firebase Console > Project Settings > Cloud Messaging > Web Push certificates');
        return null;
      }
      console.error('[FCM] Error getting token:', error);
      return null;
    }
  }

  /**
   * Request notification permission and get FCM token (legacy method)
   * For better UX, use requestNotificationPermission() early, then getToken() after login
   */
  async requestPermission(options: FcmTokenOptions = {}): Promise<string | null> {
    // Request permission first
    const permission = await this.requestNotificationPermission();
    if (permission !== 'granted') {
      return null;
    }

    // Then get token
    return await this.getToken(options);
  }

  /**
   * Save FCM token to backend via `POST /notifications/register-token`.
   * The server appends to `users.fcmTokens` (deduped) so one account can have multiple devices.
   */
  private async saveTokenToBackend(token: string): Promise<void> {
    const profile = this.authService.profile();
    if (!profile?.id) {
      console.warn('[FCM] No authenticated user to save token');
      return;
    }

    try {
      await firstValueFrom(this.apiNotifications.registerFCMToken(token));
      console.log('[FCM] Token saved to backend (PostgreSQL)');
    } catch (error: any) {
      console.error('[FCM] Error saving token to backend:', error);
      // Don't throw - token registration failure shouldn't break the app
    }
  }

  /**
   * Remove FCM token from backend (PostgreSQL) via API.
   */
  async removeToken(token: string): Promise<void> {
    const profile = this.authService.profile();
    if (!profile?.id) return;

    try {
      await firstValueFrom(this.apiNotifications.unregisterFCMToken(token));
      console.log('[FCM] Token removed from backend (PostgreSQL)');
    } catch (error) {
      console.error('[FCM] Error removing token:', error);
    }
  }

  /**
   * Listen for foreground messages.
   * Uses defer + ensureMessaging so subscriptions from child routes (e.g. vendor shell) still work
   * when they attach before AppComponent finishes await initializeWeb().
   */
  onMessage(): Observable<any> {
    return defer(() =>
      from(this.ensureMessaging()).pipe(
        switchMap((m) => {
          if (!m) {
            return EMPTY;
          }
          return new Observable<any>((subscriber) => {
            const unsubscribe = onMessage(m, (payload) => {
              console.log('[FCM] Foreground message received:', payload);
              subscriber.next(payload);
            });
            return () => {
              unsubscribe?.();
            };
          });
        }),
        catchError((error) => {
          console.error('[FCM] Error receiving message:', error);
          return EMPTY;
        }),
      ),
    );
  }

  /**
   * Initialize FCM and get token when user is authenticated
   * Assumes permission was already requested earlier (on app startup)
   */
  initializeForUser(options: FcmTokenOptions = {}): Observable<string | null> {
    return this.profile$.pipe(
      map((profile) => profile?.id ?? null),
      distinctUntilChanged(),
      switchMap((userId) => {
        if (!userId) {
          return of(null);
        }
        // Only get token; permission should be requested earlier on app startup.
        return from(this.getToken(options));
      }),
      distinctUntilChanged(),
    );
  }

  /**
   * Re-fetch web token and register with backend (idempotent for the same device).
   * Call after login or app resume so `users.fcmTokens` stays in sync.
   */
  async ensureTokenRegistered(): Promise<void> {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    await this.getToken();
  }
}

